import * as React from 'react'

import { observer } from 'mobx-react'

import { IWorkflowLocationRestriction, RestrictionType } from '~/client/graph'
import {
  DUPLICATE_WORKFLOW_SET_ERROR,
  toWorkflowChips,
  WorkflowRestrictionEditor,
} from '~/client/src/shared/components/WorkflowRestrictionEditor'
import { WorkflowTypeListSelector } from '~/client/src/shared/components/WorkflowTypeListSelector'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import type { IWorkflowRestrictionDraft } from '~/client/src/shared/resources/domain/resourceControls'
import { toast } from '~/client/src/shared/utils/toast'
import { areArraysEqualUnordered } from '~/client/src/shared/utils/util'

import { normalizeAllToEmptyIds } from '../controlKeys'
import { formatWorkflowRestriction } from '../formatWorkflowRestriction'
import EditableTagsCell from './EditableTagsCell'

interface IEditingRestriction {
  id?: string
  value: string
  // The explicit selected subset (ignored for display while `isAny`).
  workflowIds: string[]
  // "Applies to any workflow type" — persisted as []. Kept as a flag rather than
  // a materialized id list so it always covers current + future types, even if
  // types change while the editor is open.
  isAny: boolean
}

interface IProps {
  recordId: string
  restrictions: IWorkflowLocationRestriction[]
  restrictionType: RestrictionType
  label: string
  allWorkflowTypeIds: string[]
  isBookable: boolean
  getPermitTypeById: (id: string) => WorkflowType | undefined
  onSave: (recordId: string, drafts: IWorkflowRestrictionDraft[]) => void
  onDelete: (ids: string[]) => void
  /** How the editor surfaces (popover vs full-panel sheet). */
  presentation?: 'popover' | 'sheet'
}

function toDraft(
  restriction: IWorkflowLocationRestriction | undefined,
): IEditingRestriction {
  // A new restriction starts as "Any": the resource no longer carries a workflow-type
  // list of its own to seed from, and every type stays selectable in the editor.
  const workflowIds = restriction
    ? ((restriction.workflowIds ?? []) as string[])
    : []
  return {
    id: restriction?.id ?? undefined,
    value: restriction ? String(restriction.values?.[0] ?? '') : '1',
    workflowIds,
    isAny: workflowIds.length === 0,
  }
}

function WorkflowRestrictionCell({
  recordId,
  restrictions,
  restrictionType,
  label,
  allWorkflowTypeIds,
  isBookable,
  getPermitTypeById,
  onSave,
  onDelete,
  presentation,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<IEditingRestriction>(() =>
    toDraft(undefined),
  )
  const [isWorkflowListOpen, setIsWorkflowListOpen] = React.useState(false)

  // Sheet: the list opens as its own full-screen sheet, so it needs Cancel/Apply
  // to commit and go back (the selector's showActions draft). Popover: the list is
  // nested under the editor's own Save/Cancel, so it applies live (no buttons).
  const isSheet = presentation === 'sheet'
  const applyWorkflowIds = (ids: string[]) =>
    setDraft(current => ({ ...current, workflowIds: ids, isAny: false }))

  // Empty-space click adds a new restriction; clicking a tag edits that one.
  const openFor = (restriction?: IWorkflowLocationRestriction) => {
    setDraft(toDraft(restriction))
    setIsOpen(true)
  }

  // "Any" shows every current type checked (edit-down); a concrete subset shows
  // itself (possibly empty, so deselect-all works). Derived from the live all-set
  // so "Any" keeps covering newly added types.
  const effectiveWorkflowIds = React.useMemo(
    () => (draft.isAny ? allWorkflowTypeIds : draft.workflowIds),
    [draft.isAny, draft.workflowIds, allWorkflowTypeIds],
  )
  const chipWorkflows = React.useMemo(
    () => toWorkflowChips(effectiveWorkflowIds, getPermitTypeById),
    [effectiveWorkflowIds, getPermitTypeById],
  )

  const handleSave = () => {
    // Canonicalize a set for both save and duplicate comparison: resolve each id to its
    // current permit-type version (a superseded default/stored id would otherwise fail
    // the "every type = Any" check and persist a stale explicit list), then collapse
    // "every type" to [] ("Any"). Applying it to each existing restriction too keeps a
    // permit-type version change from slipping two restrictions for the same logical set
    // past the uniqueness guard. Dedupe in case two superseded ids resolve to the same id.
    const canonicalize = (ids: string[]): string[] => {
      const resolved = [
        ...new Set(ids.map(id => getPermitTypeById(id)?.id ?? id)),
      ]
      // Covers-every rather than set-equality: a saved set can carry ids outside
      // the universe (a type deleted since it was saved), and set-equality would
      // leave "everything checked" stuck as an explicit list.
      return normalizeAllToEmptyIds(resolved, allWorkflowTypeIds)
    }
    // An unnarrowed "Any" persists as [] regardless of concurrent type changes.
    const workflowIds = draft.isAny ? [] : canonicalize(draft.workflowIds)

    const isDuplicate = restrictions.some(
      restriction =>
        restriction.id !== draft.id &&
        areArraysEqualUnordered(
          workflowIds,
          canonicalize((restriction.workflowIds ?? []) as string[]),
        ),
    )
    if (isDuplicate) {
      toast.error(DUPLICATE_WORKFLOW_SET_ERROR)
      return
    }

    onSave(recordId, [
      { id: draft.id, restrictionType, values: [draft.value], workflowIds },
    ])
    setIsOpen(false)
  }

  return (
    <EditableTagsCell
      isBookable={isBookable}
      isOpen={isOpen}
      onOpen={() => openFor(undefined)}
      onClose={() => setIsOpen(false)}
      cellAffordance="plus"
      triggerLabel={label}
      presentation={presentation}
      sheetHeightMode="content"
      popoverClassName="tw:w-[360px]"
      tags={restrictions.map((restriction, index) => ({
        key: restriction.id ?? index,
        label: formatWorkflowRestriction(restriction),
        onClick: () => openFor(restriction),
        onDismiss: restriction.id
          ? () => onDelete([restriction.id as string])
          : undefined,
      }))}
      popoverContent={
        <WorkflowRestrictionEditor
          label={label}
          restrictionType={restrictionType}
          value={draft.value}
          onValueChange={event =>
            setDraft({ ...draft, value: event.target.value })
          }
          chipWorkflows={chipWorkflows}
          onWorkflowRemove={id =>
            setDraft({
              ...draft,
              workflowIds: effectiveWorkflowIds.filter(
                workflowId => workflowId !== id,
              ),
              isAny: false,
            })
          }
          isWorkflowListOpen={isWorkflowListOpen}
          onWorkflowListOpenChange={setIsWorkflowListOpen}
          workflowList={
            isSheet ? (
              <WorkflowTypeListSelector
                useActualTypes
                title={Localization.translator.selectWorkflowTypes}
                showActions
                selectedIds={effectiveWorkflowIds}
                onClose={() => setIsWorkflowListOpen(false)}
                onApply={ids => {
                  applyWorkflowIds(ids)
                  setIsWorkflowListOpen(false)
                }}
              />
            ) : (
              <WorkflowTypeListSelector
                useActualTypes
                title={Localization.translator.selectWorkflowTypes}
                onClose={() => setIsWorkflowListOpen(false)}
                selectedIds={effectiveWorkflowIds}
                onSelectionChange={applyWorkflowIds}
              />
            )
          }
          onSave={handleSave}
          onCancel={() => setIsOpen(false)}
          layout={presentation}
          // A draft with an id came from clicking a chip (edit); without one it's
          // the "+"/empty-space add — the title tells the two apart.
          title={
            draft.id
              ? Localization.translator.editX(label)
              : Localization.translator.createNewX(label)
          }
        />
      }
    />
  )
}

export default observer(WorkflowRestrictionCell)
